import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { PvTrackerType, SolarModule } from '../SystemSettingsUtils'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import { determineAllowedTrackersBySerialNumber, isSolarSettingsHiddenForProductType } from 'src/Libs/solar/utils'
import { AccordionItem, AccordionItemContent } from '@e3dc-react/shell/Elements/Accordion'
import { default as useAccordionStyle } from 'src/Libs/styles'
import useSolar from 'src/Libs/solar/hooks/use-solar'
import { useUserRoles } from 'src/Hooks/user'
import { MultipleToggleSettingBox } from '../forms'
import SystemSettingsBlankView from 'src/Libs/system-settings/component/SystemSettingsBlankView'
import { TabButtonOption } from 'src/Components/TabButton/TabButtonGroup'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'

interface SolarSettingsProps {
  solarModule: SolarModule
  storageId?: string
  storageIdServer?: string
  isReadOnlyAccess?: boolean
}

/**
 * Provides the configurations for a PV-Inverter
 *
 * The component handles a special case for P20 installations.
 * They can only be identified by their serial number. They start with the digits "85".
 *
 * Not shown for installations with an EmsProductType where tracker settings don't apply
 * ('S20 X Pro', 'one', 'S10 SE'), see isSolarSettingsHiddenForProductType.
 *
 * @param solarModule
 * @param storageId
 * @constructor
 */
const SolarSettings: React.FC<SolarSettingsProps> = ({ solarModule, storageId, storageIdServer, isReadOnlyAccess = true }) => {
  const { classes: accordionClasses } = useAccordionStyle()
  const { t } = useCustomTranslation()

  const { isUser } = useUserRoles()
  const { storage } = useActiveInstallationContext()

  const { solarModules, onChangeTracker, activeTracker } = useSolar(storageId, storageIdServer)

  const currentModule = useMemo(() => {
    return solarModules?.find(solarModule => solarModule.storageId === storageId) ?? solarModule
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solarModules, storageId])

  const allowedPvTrackers = determineAllowedTrackersBySerialNumber(Number(storageId))
  const isRestrictedToReadAccess = isUser || isReadOnlyAccess

  const pvTrackerButtonActions = useMemo<TabButtonOption[]>(
    () =>
      allowedPvTrackers.map((pvTrackerType, index) => ({
        id: `tracker-type-${pvTrackerType}`,
        label: t(`systemSettings.solar.settings.tracker-type-${pvTrackerType}`),
        onChange: () => {
          onChangeTracker(storageId, index, currentModule.pviIndex, pvTrackerType as PvTrackerType)
        },
      })) as TabButtonOption[],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentModule?.pviIndex, currentModule.pviTracker, storageId, onChangeTracker]
  )

  const description = (
    <>
      <Typography variant={'body2'}>{t('systemSettings.solar.settings.singleTrackerText')}</Typography>
      <Typography variant={'body2'}>{t('systemSettings.solar.settings.duoTrackerText')}</Typography>
    </>
  )

  if (allowedPvTrackers.length === 1 && allowedPvTrackers[0] === PvTrackerType.triple) return null
  if (isSolarSettingsHiddenForProductType(storage?.product)) return null

  return (
    <AccordionItem title={t('systemSettings.solar.settings.title')} className={accordionClasses.accordion}>
      <AccordionItemContent variant="paper">
        {solarModule ? (
          <Box sx={{ mt: 2 }}>
            <MultipleToggleSettingBox
              title={t('systemSettings.solar.settings.tracker-title')}
              actions={pvTrackerButtonActions}
              activeIndex={activeTracker}
              disabled={isRestrictedToReadAccess}
              tooltipKey="systemSettings.access.only-read-access"
              name={'pvTracker'}
              description={description}
            />
          </Box>
        ) : (
          <SystemSettingsBlankView />
        )}
      </AccordionItemContent>
    </AccordionItem>
  )
}

export default SolarSettings
